import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Offcanvas, { OffcanvasProps } from 'react-bootstrap/Offcanvas';

import CloseButton from '../CloseButton';
import WorkspaceBotoes from '../WorkspaceBotoes';
import {
  Body,
  Footer,
  FooterContentLeft,
  FooterContentRight,
  Header,
  HeaderTitle,
  OffcanvasContainer,
} from './styles';

type HorizontalPlacement = 'start' | 'end';

type RemainingOffcanvasProps = Omit<
  OffcanvasProps,
  'children' | 'show' | 'onHide' | 'placement'
>;

type OffcanvasStyle =
  | (React.CSSProperties & {
      '--bs-offcanvas-width'?: string;
    })
  | undefined;

export interface CustomOffcanvasProps extends RemainingOffcanvasProps {
  show: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string;
  footerContent?: React.ReactNode;
  showOpenButton?: boolean;
  openRoute?: string;
  style?: React.CSSProperties;
  placement?: HorizontalPlacement;
  titleId?: string;
  closeButtonAriaLabel?: string;
  autoFocusTitle?: boolean;
  'data-testid'?: boolean | string;
}

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
    .toLowerCase();

const CustomOffcanvas: React.FC<CustomOffcanvasProps> = ({
  show,
  onClose,
  title,
  children,
  width,
  footerContent,
  showOpenButton = true,
  openRoute,
  style,
  placement = 'end',
  titleId,
  closeButtonAriaLabel,
  autoFocusTitle,
  'data-testid': dataTestId,
  ...restProps
}) => {
  const computedStyle: OffcanvasStyle = useMemo(() => {
    if (!style && !width) {
      return undefined;
    }

    const nextStyle: Exclude<OffcanvasStyle, undefined> = {
      ...(style ?? {}),
    };

    if (width) {
      nextStyle['--bs-offcanvas-width'] = width;
      nextStyle.width = width;
    }

    return nextStyle;
  }, [style, width]);

  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId
        ? `offcanvas-${slugify(title || titleId || placement)}`
        : undefined;

  const titleRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (autoFocusTitle && show) {
      titleRef.current?.focus();
    }
  }, [autoFocusTitle, show]);

  const handleOpenRoute = useCallback(() => {
    if (openRoute) {
      window.open(openRoute, '_blank', 'noopener,noreferrer');
    }
  }, [openRoute]);

  const canShowOpenButton = showOpenButton && Boolean(openRoute);

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement={placement}
      keyboard
      backdrop
      style={computedStyle}
      aria-labelledby={titleId}
      data-testid={testId}
      {...restProps}
    >
      <OffcanvasContainer
        data-testid={testId ? `${testId}-container` : undefined}
      >
        <Header>
          <HeaderTitle
            id={titleId}
            tabIndex={autoFocusTitle ? -1 : undefined}
            ref={titleRef}
            data-testid={testId ? `${testId}-title` : undefined}
          >
            {title}
          </HeaderTitle>
          <CloseButton
            ariaLabel={closeButtonAriaLabel ?? 'Fechar painel lateral'}
            onClick={onClose}
            style={{
              backgroundColor: '#ffffff',
              padding: '4px',
              fontSize: '0.8rem',
            }}
          />
        </Header>

        <Body data-testid={testId ? `${testId}-body` : undefined}>
          {children}
        </Body>

        <Footer data-testid={testId ? `${testId}-footer` : undefined}>
          {footerContent && (
            <FooterContentLeft
              data-testid={testId ? `${testId}-footer-left` : undefined}
            >
              {footerContent}
            </FooterContentLeft>
          )}

          {canShowOpenButton && (
            <FooterContentRight
              data-testid={testId ? `${testId}-footer-right` : undefined}
            >
              <WorkspaceBotoes
                open
                onClickOpen={handleOpenRoute}
                data-testid={testId ? `${testId}-open-route` : undefined}
              />
            </FooterContentRight>
          )}
        </Footer>
      </OffcanvasContainer>
    </Offcanvas>
  );
};

export default CustomOffcanvas;
